/**
 * TodayView — kanban-style task board (composition only, stateful).
 *
 * Per FWV v8 §30: this file owns state + effects. All rendering lives in
 * TodayViewCanvas. State machines live in useKanbanSelection / useRectangleDrag.
 * Position math lives in computeInsertionPosition.
 *
 * Interactions:
 *  - Plain click on card body → open task in peek panel (NOT advance)
 *  - Click the "→ Start" button → advance to next status
 *  - Shift/cmd/ctrl-click → toggle in multi-selection
 *  - Drag card onto column → move to the position shown by the insertion line
 *  - Drag rectangle → bulk-select intersecting cards
 */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { fetchTasks, createTask, type DbTask } from '../../db'
import { devDb } from '../../devMode'
import { useApp } from '../../store'
import { useKanbanSelection } from './useKanbanSelection'
import { buildRectangleDragRef, type Rect } from './useRectangleDrag'
import { computeInsertionPosition } from './computeInsertionPosition'
import { AddTaskForm } from './AddTaskForm'
import { TodayViewCanvas, COLUMNS, type ColumnTaskMap } from './TodayViewCanvas'

function nextStatus(s: DbTask['status']): DbTask['status'] {
  if (s === 'todo') return 'in_progress'
  if (s === 'in_progress') return 'pending_approval'
  if (s === 'pending_approval') return 'done'
  return s
}

export function TodayView() {
  const { currentMember, dev, openPeek } = useApp()
  const [tasks, setTasks] = useState<DbTask[]>([])
  const [loading, setLoading] = useState(true)
  const [rect, setRect] = useState<Rect>(null)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [dragCursorY, setDragCursorY] = useState<number | null>(null)
  const lastInsertionRef = useRef<{ status: DbTask['status']; index: number } | null>(null)

  // 'My stuff' filter — when set, only show tasks assigned to this member
  const [filterAssignee, setFilterAssignee] = useState<string | null>(null)

  const selection = useKanbanSelection()

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data } = dev ? await devDb.tasks.list() : await fetchTasks('family')
    setTasks(data ?? [])
    setLoading(false)
  }

  // Pre-sorted task map (sorted by position ascending) for the canvas.
  // Honors the 'My stuff' filter when filterAssignee is set.
  const tasksByStatus = useMemo<ColumnTaskMap>(() => {
    const map: ColumnTaskMap = { todo: [], in_progress: [], pending_approval: [], done: [], archived: [] }
    const filtered = filterAssignee
      ? tasks.filter((t) => t.assignees.includes(filterAssignee))
      : tasks
    for (const t of filtered) {
      if (map[t.status]) map[t.status].push(t)
    }
    for (const k of Object.keys(map) as DbTask['status'][]) {
      map[k].sort((a, b) => a.position - b.position)
    }
    return map
  }, [tasks, filterAssignee])

  const advance = useCallback(
    async (task: DbTask) => {
      const next = nextStatus(task.status)
      if (dev) await devDb.tasks.updateStatus(task.id, next)
      load()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dev]
  )

  const moveMany = useCallback(
    async (ids: string[], target: DbTask['status'], position: number) => {
      for (const id of ids) {
        if (dev) await devDb.tasks.move(id, target, position)
      }
      selection.clear()
      load()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dev]
  )

  // Action-bar moves append to the end (no cursor info available)
  const actionBarMove = useCallback(
    (target: DbTask['status']) => {
      const destTasks = tasksByStatus[target]
      const newPos = computeInsertionPosition(destTasks, destTasks.length)
      moveMany(selection.ids, target, newPos)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasksByStatus, selection.ids]
  )

  const onCardClick = useCallback(
    (e: React.MouseEvent, task: DbTask) => {
      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        e.preventDefault()
        selection.toggle(task.id)
        return
      }
      openPeek({ kind: 'task', id: task.id })
    },
    [selection, openPeek]
  )

  const onAddTask = useCallback(
    async (title: string, ep: number, recurrence: DbTask['recurrence']) => {
      if (!currentMember) return
      const payload = {
        title, type: 'chore' as const, priority: 'med' as const,
        ep, assignees: [currentMember.id], status: 'todo' as const,
        position: tasks.length, created_by: currentMember.id,
        recurrence, recurrence_parent_id: null,
      }
      if (dev) await devDb.tasks.create(payload)
      else await createTask(payload)
      load()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dev, currentMember]
  )

  const setContainer = buildRectangleDragRef({
    setRect,
    onSelect: (ids) => selection.replace(ids),
    clearSelectionOnStart: () => selection.clear(),
  })

  // Stable per-column insertion callbacks. Without useCallback, the inline arrow
  // would change on every render, causing KanbanColumn's useEffect to re-fire.
  const onInsertionChange = useCallback(
    (status: DbTask['status']) => (idx: number | null) => {
      if (idx === null) {
        if (lastInsertionRef.current?.status === status) lastInsertionRef.current = null
      } else {
        lastInsertionRef.current = { status, index: idx }
      }
    },
    []
  )

  function onDragStart() {
    setActiveDragId(String(arguments[0].active.id))
    setDragCursorY(null)
    lastInsertionRef.current = null
  }
  function onDragMove(e: import('@dnd-kit/core').DragMoveEvent) {
    const activator = e.activatorEvent as PointerEvent | undefined
    if (activator && e.delta) setDragCursorY(activator.clientY + e.delta.y)
  }
  function onDragEnd(e: import('@dnd-kit/core').DragEndEvent) {
    setActiveDragId(null)
    setDragCursorY(null)
    const cardId = String(e.active.id)
    const overData = e.over?.data?.current as { status?: DbTask['status'] } | undefined
    const targetStatus = overData?.status
    if (!targetStatus) { lastInsertionRef.current = null; return }
    const destTasks = tasksByStatus[targetStatus]
    const li = lastInsertionRef.current
    const idx = li?.status === targetStatus ? li.index : destTasks.length
    const newPos = computeInsertionPosition(destTasks, idx)
    const ids = selection.has(cardId) ? selection.ids : [cardId]
    moveMany(ids, targetStatus, newPos)
    lastInsertionRef.current = null
  }
  function onDragCancel() {
    setActiveDragId(null)
    setDragCursorY(null)
    lastInsertionRef.current = null
  }

  const activeTask = activeDragId ? tasks.find((t) => t.id === activeDragId) : null

  return (
    <>
      <AddTaskForm onSubmit={onAddTask} />
      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <TodayViewCanvas
          tasksByStatus={tasksByStatus}
          hasSelection={selection.has}
          activeDragId={activeDragId}
          dragCursorY={dragCursorY}
          rect={rect}
          selectionCount={selection.ids.length}
          activeTask={activeTask}
          filterAssignee={filterAssignee}
          currentMember={currentMember}
          onContainerRef={setContainer}
          onCardClick={onCardClick}
          onCardAdvance={advance}
          onInsertionChangeByStatus={onInsertionChange}
          onDragStart={onDragStart}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
          onActionBarMove={actionBarMove}
          onClearSelection={selection.clear}
          onSelectFromRectangle={(ids) => selection.replace(ids)}
          onSetFilterAssignee={setFilterAssignee}
        />
      )}
    </>
  )
}

// Re-export so other surfaces can see the kanban columns if needed
export { COLUMNS }